import React, { useState, useMemo } from 'react';
import Button from '@app/react-common/components/button/Button';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import { StepperInput } from '@app/react-common/components/stepper-input/StepperInput';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { ADJUST_BY_TOOLTIP } from '../utils/consts';
import { SelectedMethodInfo } from '../BulkEditModal';

interface ShippingRatesEditViewProps {
  onBack: () => void;
  onClose: () => void;
  onPreviewChanges: (data: ShippingRatesFormData) => void;
  currencySign: string;
  selectedMethods: SelectedMethodInfo[];
}

export interface ShippingRatesFormData {
  adjustBy: number;
  keepFreeShippingFree: boolean;
}

export const ShippingRatesEditView = ({
  onBack,
  onClose,
  onPreviewChanges,
  currencySign,
  selectedMethods,
}: ShippingRatesEditViewProps) => {
  const [adjustBy, setAdjustBy] = useState<number>(0);
  const [isValid, setIsValid] = useState(true);
  const [keepFreeShippingFree, setKeepFreeShippingFree] = useState(true);

  // Find the smallest positive rate value across all selected methods
  // This determines how much we can subtract without going below 0
  const smallestRateValue = useMemo(() => {
    const values: number[] = [];

    for (const method of selectedMethods) {
      // Add main rate (only if positive, since 0 means free shipping)
      const mainRate = Number(method.rate);
      if (method.rate != null && mainRate > 0) {
        values.push(mainRate);
      }
      // Check additional price ranges
      if (method.additionalPriceRanges && Array.isArray(method.additionalPriceRanges)) {
        for (const range of method.additionalPriceRanges) {
          const rangeRate = Number(range.rate);
          if (range.rate != null && rangeRate > 0) {
            values.push(rangeRate);
          }
        }
      }
    }

    return values.length > 0 ? Math.min(...values) : null;
  }, [selectedMethods]);

  const wouldGoBelowZero = adjustBy < 0 && smallestRateValue !== null && smallestRateValue + adjustBy < 0;

  const handlePreviewChanges = () => {
    onPreviewChanges({ adjustBy, keepFreeShippingFree });
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Shipping Rates</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">
            Adjust By
            <Tooltip tooltipText={ADJUST_BY_TOOLTIP} appearOnSide="bottom" maxWidth={200}>
              <i className="icon icon-more_information" />
            </Tooltip>
          </label>
          <div className="bulk-edit-modal__stepper-wrapper">
            <span className="bulk-edit-modal__currency-prefix">{currencySign}</span>
            <StepperInput
              presetValue={adjustBy}
              onChange={setAdjustBy}
              setValid={setIsValid}
              disabled={false}
              min={Number.NEGATIVE_INFINITY}
            />
          </div>
        </div>
        <div className="bulk-edit-modal__field">
          <Checkbox checked={keepFreeShippingFree} onChange={setKeepFreeShippingFree} label="Keep Free Shipping Free" />
        </div>
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {!isValid || adjustBy === 0 || wouldGoBelowZero ? (
          <Tooltip
            tooltipText={
              wouldGoBelowZero
                ? `Adjustment would result in negative rates. Maximum reduction is ${currencySign}${smallestRateValue?.toFixed(2)}`
                : 'Enter an adjustment value to preview changes'
            }
            maxWidth={250}>
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" />
          </div>
        )}
      </div>
    </div>
  );
};
